import React, { useEffect, useState } from 'react';
import {
	Box,
	Card,
	CardContent,
	CardHeader,
	CircularProgress,
	IconButton,
	Tooltip,
	Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { LineChart } from '@mui/x-charts/LineChart';
import {
	addYears,
	endOfMonth,
	format,
	setMonth,
	startOfMonth,
	startOfYear,
} from 'date-fns';
import { getTicketStatsForParkingLocation } from 'api/canpark/tickets/getTicketStatsForParkingLocation';
import { ParkingLocation } from 'containers/pages/CANpark/types/parkingLocation';
import { useTicketTypeMeta } from 'containers/pages/CANpark/context/TicketTerminologyProvider';

interface MonthPoint {
	month: string;
	logs: number;
	tags: number;
	tows: number;
}

const CHART_HEIGHT = 300;

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const CustomStackedAreaChart: React.FC<{
	parkingLocation: ParkingLocation;
}> = ({ parkingLocation }) => {
	const getTicketTypeMeta = useTicketTypeMeta();
	const [yearOffset, setYearOffset] = useState(0);
	const [data, setData] = useState<MonthPoint[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			try {
				setLoading(true);
				setError(null);
				const now = new Date();
				const start = startOfYear(setMonth(now, 0));
				const displayStart = addYears(start, -yearOffset);
				const promises = Array.from({ length: 12 }, (_, i) => {
					const monthStart = startOfMonth(setMonth(displayStart, i));
					const monthEnd = endOfMonth(monthStart);
					return getTicketStatsForParkingLocation(
						parkingLocation.id,
						monthStart,
						monthEnd,
					);
				});
				const stats = await Promise.all(promises);
				if (cancelled) return;
				setData(
					stats.map((s, i) => ({
						month: format(setMonth(displayStart, i), 'MMM'),
						logs: parseInt(s.logTickets),
						tags: parseInt(s.logAndWarnTickets),
						tows: parseInt(s.towTickets),
					})),
				);
			} catch {
				if (!cancelled) setError('Failed to load data');
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		fetchData();
		return () => {
			cancelled = true;
		};
	}, [parkingLocation.id, yearOffset]);

	const year = new Date().getFullYear() - yearOffset;
	const isEmpty =
		data.length > 0 &&
		data.every((d) => d.logs === 0 && d.tags === 0 && d.tows === 0);

	return (
		<Card sx={{ width: '100%', maxWidth: 450 }}>
			<CardHeader title="Monthly Violations" sx={{ pb: 0 }} />
			<CardContent sx={{ pt: 1 }}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						mb: 1,
						gap: 1,
					}}
				>
					<Tooltip title="Previous year">
						<IconButton
							size="small"
							onClick={() => setYearOffset(yearOffset + 1)}
						>
							<ChevronLeft />
						</IconButton>
					</Tooltip>
					<Typography variant="caption" color="text.secondary">
						{year}
					</Typography>
					<Tooltip
						title={
							yearOffset === 0
								? 'Already at the latest year'
								: 'Next year'
						}
					>
						<span>
							<IconButton
								size="small"
								onClick={() => setYearOffset(yearOffset - 1)}
								disabled={yearOffset === 0}
							>
								<ChevronRight />
							</IconButton>
						</span>
					</Tooltip>
				</Box>
				{loading ? (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							height: CHART_HEIGHT,
						}}
					>
						<CircularProgress />
					</Box>
				) : error ? (
					<Typography
						color="error"
						sx={{ textAlign: 'center', py: 4 }}
					>
						{error}
					</Typography>
				) : isEmpty ? (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							height: CHART_HEIGHT,
						}}
					>
						<Typography variant="body2" color="text.secondary">
							No violations this year
						</Typography>
					</Box>
				) : (
					<LineChart
						height={CHART_HEIGHT}
						xAxis={[
							{
								scaleType: 'band',
								data: data.map((d) => d.month),
							},
						]}
						yAxis={[{ tickMinStep: 1 }]}
						series={[
							{
								data: data.map((d) => d.tows),
								label: cap(getTicketTypeMeta('tow').pluralLabel),
								area: true,
								stack: 'total',
								showMark: false,
								color: getTicketTypeMeta('tow').hexColor,
							},
							{
								data: data.map((d) => d.tags),
								label: cap(
									getTicketTypeMeta('logAndWarn').pluralLabel,
								),
								area: true,
								stack: 'total',
								showMark: false,
								color: getTicketTypeMeta('logAndWarn').hexColor,
							},
							{
								data: data.map((d) => d.logs),
								label: cap(getTicketTypeMeta('log').pluralLabel),
								area: true,
								stack: 'total',
								showMark: false,
								color: getTicketTypeMeta('log').hexColor,
							},
						]}
						margin={{ top: 16, right: 16, left: 0, bottom: 24 }}
					/>
				)}
			</CardContent>
		</Card>
	);
};

export default CustomStackedAreaChart;
